import {
  Badge,
  Button,
  Card,
  Field,
  Flex,
  HStack,
  Heading,
  InfoBox,
  Input,
  RefreshButton,
  Stack,
  Switch,
  Text,
  notify,
} from "components/ui";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { SetupGuideLink } from "components/common/SetupGuideLink";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit, MdSend } from "react-icons/md";
import {
  CUSTOMER_COMPANY_NOTIFICATION_PROVIDERS,
  CustomerCompanyNotificationChannel,
  CustomerCompanyNotificationProvider,
  customerCompanyAdminService,
} from "services/customerCompanyAdminService";
import { getThemeColor } from "theme/colors";
import { ChatApprovalLimitCard, InboundSetupSection } from "./ChatAppSettings";
import {
  ChannelFormState,
  EMPTY_CHANNEL_FORM,
  SlackFields,
  SmtpFields,
  TelegramFields,
  buildChannelSettings,
  channelFormFrom,
  defaultChannelName,
  slackChannelStatus,
  validateChannelForm,
} from "./IntegrationChannelForms";
import { TeamsFields, teamsChannelStatus } from "./TeamsChannelFields";

interface IntegrationsTabProps {
  customerCompanyId: string;
}

/** Which provider the dialog is editing, and the channel it belongs to. */
interface EditorTarget {
  provider: CustomerCompanyNotificationProvider;
  /** Null when adding — the channel does not exist yet. */
  channel: CustomerCompanyNotificationChannel | null;
}

/**
 * This company's own notification channels — at most one per provider (SMTP,
 * Telegram, Slack, Teams). When a provider is configured and active here it takes
 * precedence over the tenant-wide channel for this company; when absent or
 * inactive, notifications fall back to the tenant channel.
 */
const IntegrationsTab = ({ customerCompanyId }: IntegrationsTabProps) => {
  const { t } = useTranslation();

  const [channels, setChannels] = useState<
    CustomerCompanyNotificationChannel[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [form, setForm] = useState<ChannelFormState>(EMPTY_CHANNEL_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] =
    useState<CustomerCompanyNotificationChannel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [testEmail, setTestEmail] = useState("");
  const [testingId, setTestingId] = useState<string | null>(null);

  const providerLabel = (provider: CustomerCompanyNotificationProvider) =>
    t(`app.customerCompanies.integrations.provider.${provider.toLowerCase()}`, {
      defaultValue: provider,
    });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setChannels(
        await customerCompanyAdminService.listNotificationChannels(
          customerCompanyId,
        ),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customerCompanies.integrations.loadFailed", {
            defaultValue: "Failed to load the notification channel.",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reportFailure = (err: any) =>
    notify({
      type: "error",
      title: t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      description: err.response?.data?.error,
    });

  const openAdd = (provider: CustomerCompanyNotificationProvider) => {
    setForm({ ...EMPTY_CHANNEL_FORM, name: defaultChannelName(provider) });
    setFormError(null);
    setEditor({ provider, channel: null });
  };

  const openEdit = (channel: CustomerCompanyNotificationChannel) => {
    setForm(channelFormFrom(channel));
    setFormError(null);
    setEditor({ provider: channel.providerType, channel });
  };

  const patchForm = (patch: Partial<ChannelFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!editor) return;
    const invalid = validateChannelForm(
      form,
      editor.provider,
      editor.channel !== null,
      t,
      editor.channel ? channelFormFrom(editor.channel) : null,
    );
    if (invalid) {
      setFormError(invalid);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const settings = buildChannelSettings(form, editor.provider);
      const saved = editor.channel
        ? await customerCompanyAdminService.updateNotificationChannel(
            customerCompanyId,
            editor.channel.id,
            { name: form.name.trim(), ...settings },
          )
        : await customerCompanyAdminService.createNotificationChannel(
            customerCompanyId,
            {
              name: form.name.trim(),
              providerType: editor.provider,
              ...settings,
            },
          );
      setChannels((prev) =>
        prev.some((c) => c.id === saved.id)
          ? prev.map((c) => (c.id === saved.id ? saved : c))
          : [...prev, saved],
      );
      // A new Slack channel without a token goes straight on to its manifest.
      if (
        !editor.channel &&
        saved.providerType === "Slack" &&
        !saved.slackSettings?.hasBotToken
      ) {
        openEdit(saved);
      } else {
        setEditor(null);
      }
      notify({
        type: "success",
        title: t("app.customerCompanies.integrations.saved", {
          defaultValue: "Notification channel saved.",
        }),
      });
    } catch (err: any) {
      setFormError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (
    channel: CustomerCompanyNotificationChannel,
  ) => {
    setBusyId(channel.id);
    try {
      const saved =
        await customerCompanyAdminService.toggleNotificationChannelActive(
          customerCompanyId,
          channel.id,
        );
      setChannels((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await customerCompanyAdminService.deleteNotificationChannel(
        customerCompanyId,
        deleteTarget.id,
      );
      setChannels((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      notify({
        type: "success",
        title: t("app.customerCompanies.integrations.deleted", {
          defaultValue: "Notification channel deleted.",
        }),
      });
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleTest = async (channel: CustomerCompanyNotificationChannel) => {
    setTestingId(channel.id);
    try {
      const result = await customerCompanyAdminService.testNotificationChannel(
        customerCompanyId,
        channel.id,
        // Only SMTP delivers somewhere; the other providers just verify the token.
        channel.providerType === "Smtp"
          ? testEmail.trim() || undefined
          : undefined,
      );
      notify({
        type: result.success ? "success" : "error",
        title: result.success
          ? t("app.customerCompanies.integrations.testSuccess", {
              defaultValue: "Test email sent.",
            })
          : t("app.customerCompanies.integrations.testFailed", {
              defaultValue: "Test email failed.",
            }),
        description: result.error ?? undefined,
      });
      // A test flips the channel between Active and Error — reload to show it.
      await load();
    } catch (err: any) {
      reportFailure(err);
    } finally {
      setTestingId(null);
    }
  };

  /** The one-line detail under a channel's name — what it delivers through. */
  const channelDetail = (channel: CustomerCompanyNotificationChannel) => {
    if (channel.providerType === "Smtp" && channel.smtpSettings) {
      const smtp = channel.smtpSettings;
      const target = smtp.displayName
        ? `${smtp.displayName} <${smtp.fromEmail ?? ""}>`
        : (smtp.fromEmail ?? "");
      return [
        `${smtp.host}:${smtp.port}${smtp.enableSsl ? " (SSL)" : ""}`,
        target,
      ].filter(Boolean);
    }
    if (channel.providerType === "Telegram") {
      return [
        channel.telegramSettings?.botUsername
          ? `@${channel.telegramSettings.botUsername}`
          : t("app.customerCompanies.integrations.tokenStored", {
              defaultValue: "Token stored",
            }),
      ];
    }
    if (channel.providerType === "Slack") {
      return [slackChannelStatus(channel, t)];
    }
    if (channel.providerType === "Teams") {
      return [teamsChannelStatus(channel, t)];
    }
    return [];
  };

  const missingProviders = CUSTOMER_COMPANY_NOTIFICATION_PROVIDERS.filter(
    (provider) => !channels.some((c) => c.providerType === provider),
  );

  const cardProps = {
    borderWidth: "1px",
    borderColor: getThemeColor("card.border"),
    borderRadius: "md",
  } as const;

  return (
    <Stack gap={4}>
      <Card.Root {...cardProps}>
        <Card.Body>
          <Stack gap={3}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <Stack gap={1}>
                <Heading size="sm">
                  {t("app.customerCompanies.integrations.title", {
                    defaultValue: "Integrations",
                  })}
                </Heading>
                <Text fontSize="sm" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.integrations.subtitleMulti", {
                    defaultValue:
                      "The channels this company's notifications are delivered through. Without one, the tenant-wide channel is used.",
                  })}
                </Text>
              </Stack>
              <HStack gap={2}>
                <SetupGuideLink doc="notification-channels-setup" />
                <RefreshButton onClick={load} loading={loading} />
              </HStack>
            </Flex>

            {error && (
              <InfoBox variant="error">
                <Text color="red.700">{error}</Text>
              </InfoBox>
            )}

            {loading ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.integrations.loading", {
                  defaultValue: "Loading channel…",
                })}
              </Text>
            ) : channels.length === 0 ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.integrations.notConfigured", {
                  defaultValue:
                    "No mail server configured. This company's notifications use the tenant-wide channel.",
                })}
              </Text>
            ) : (
              <Stack gap={5}>
                {channels.map((channel) => (
                  <Stack key={channel.id} gap={3}>
                    <HStack gap={2} wrap="wrap">
                      <Text fontWeight="medium">{channel.name}</Text>
                      <Badge
                        colorPalette={channel.isActive ? "green" : "gray"}
                        variant="subtle"
                      >
                        {channel.isActive
                          ? t("app.common.active", { defaultValue: "Active" })
                          : t("app.common.inactive", {
                              defaultValue: "Inactive",
                            })}
                      </Badge>
                      <Badge variant="subtle">
                        {providerLabel(channel.providerType)}
                      </Badge>
                    </HStack>

                    <Stack gap={1}>
                      {channelDetail(channel).map((line) => (
                        <Text
                          key={line}
                          fontSize="sm"
                          color="var(--text-secondary-color)"
                        >
                          {line}
                        </Text>
                      ))}
                    </Stack>

                    <HStack gap={3} align="center" wrap="wrap">
                      <HStack gap={2}>
                        <Text fontSize="sm">
                          {t("app.common.active", { defaultValue: "Active" })}
                        </Text>
                        <Switch
                          checked={channel.isActive}
                          disabled={busyId === channel.id}
                          onCheckedChange={() => handleToggleActive(channel)}
                        />
                      </HStack>
                      <Button intent="ghost" onClick={() => openEdit(channel)}>
                        <MdEdit />{" "}
                        {t("app.common.edit", { defaultValue: "Edit" })}
                      </Button>
                      <Button
                        intent="ghost"
                        onClick={() => setDeleteTarget(channel)}
                      >
                        <MdDelete />{" "}
                        {t("app.common.delete", { defaultValue: "Delete" })}
                      </Button>
                    </HStack>

                    <HStack gap={2} wrap="wrap">
                      {channel.providerType === "Smtp" && (
                        <Input
                          placeholder={t(
                            "app.customerCompanies.integrations.testEmailLabel",
                            { defaultValue: "Send a test email to…" },
                          )}
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          flex={1}
                          minW="220px"
                        />
                      )}
                      <Button
                        intent="secondary"
                        onClick={() => handleTest(channel)}
                        disabled={testingId === channel.id}
                      >
                        <MdSend />{" "}
                        {testingId === channel.id
                          ? t("app.customerCompanies.integrations.testing", {
                              defaultValue: "Sending...",
                            })
                          : t("app.customerCompanies.integrations.test", {
                              defaultValue: "Send test",
                            })}
                      </Button>
                    </HStack>
                  </Stack>
                ))}
              </Stack>
            )}

            {!loading && missingProviders.length > 0 && (
              <Stack gap={2}>
                <Text fontSize="xs" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.integrations.perProviderHint", {
                    defaultValue: "One channel per provider.",
                  })}
                </Text>
                <HStack gap={2} wrap="wrap">
                  {missingProviders.map((provider) => (
                    <Button
                      key={provider}
                      intent="secondary"
                      onClick={() => openAdd(provider)}
                    >
                      <MdAdd />{" "}
                      {t("app.customerCompanies.integrations.addChannel", {
                        defaultValue: "Add {{provider}}",
                        provider: providerLabel(provider),
                      })}
                    </Button>
                  ))}
                </HStack>
              </Stack>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      <ChatApprovalLimitCard customerCompanyId={customerCompanyId} />

      <DialogRoot
        open={editor !== null}
        onOpenChange={(e: { open: boolean }) =>
          e.open ? undefined : setEditor(null)
        }
        size="lg"
      >
        <DialogContent>
          <DialogHeader>
            <Flex justify="space-between" align="center" w="full" gap={3}>
              <DialogTitle>
                {editor ? providerLabel(editor.provider) : ""}
              </DialogTitle>
              {editor && (
                <SetupGuideLink
                  doc="notification-channels-setup"
                  section={editor.provider.toLowerCase()}
                  intent="secondary"
                  label={t("app.common.whereDoIFindThese")}
                />
              )}
            </Flex>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              {formError && (
                <InfoBox variant="error">
                  <Text color="red.700">{formError}</Text>
                </InfoBox>
              )}

              {editor?.channel && (
                <InboundSetupSection
                  channel={editor.channel}
                  onDownloadTeamsPackage={() =>
                    customerCompanyAdminService.downloadTeamsPackage(
                      customerCompanyId,
                      editor.channel!.id,
                    )
                  }
                />
              )}

              <Field
                label={t("app.customerCompanies.integrations.fields.name", {
                  defaultValue: "Channel name",
                })}
                required
              >
                <Input
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  maxLength={200}
                />
              </Field>

              {editor?.provider === "Smtp" && (
                <SmtpFields
                  form={form}
                  onChange={patchForm}
                  existing={editor.channel !== null}
                  t={t}
                />
              )}
              {editor?.provider === "Telegram" && (
                <TelegramFields
                  form={form}
                  onChange={patchForm}
                  existing={editor.channel !== null}
                  t={t}
                />
              )}
              {editor?.provider === "Slack" && (
                <SlackFields
                  form={form}
                  onChange={patchForm}
                  existing={editor.channel !== null}
                  slackSettings={editor.channel?.slackSettings}
                  t={t}
                />
              )}
              {editor?.provider === "Teams" && (
                <TeamsFields
                  form={form}
                  onChange={patchForm}
                  existing={editor.channel !== null}
                  t={t}
                />
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setEditor(null)}>
              {t("app.common.cancel")}
            </Button>
            <Button intent="primary" onClick={handleSave} disabled={saving}>
              {saving
                ? t("app.common.saving", { defaultValue: "Saving..." })
                : t("app.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t("app.customerCompanies.integrations.deleteTitle", {
          defaultValue: "Delete notification channel",
        })}
        message={t("app.customerCompanies.integrations.deleteConfirm", {
          defaultValue:
            "This company's notifications will fall back to the tenant-wide channel.",
        })}
      />
    </Stack>
  );
};

export default IntegrationsTab;
